import { useMemo } from 'react';

import { useUserStore, userStoreSelector } from '@editor/stores/userStore';
import { MinimumWorkspace, useWorkspacesForUserQuery } from '@repo/api-client';

import { useWorkspacesFeatureEnabled } from 'global/growthbookUtils';
import { useActiveWorkspaceId } from 'global/utils/workspace/useActiveWorkspaceId';

/**
 * This hook gets all the workspaces for the WorkspaceSelector.
 *
 * Its only moved to a separate file instead of part of WorkspaceSelector so we can use it to determine
 * if we should render the WorkspaceSelector or the TeamSelector
 *
 * @returns an array of the current workspace and all other workspaces or null if the user is not logged in,
 * does not have the FF for workspaces or the workspaces are still loading
 */
export const useWorkspacesForWorkspaceSelector = () => {
  const isEnabled = useWorkspacesFeatureEnabled();
  const { isLoading: isLoadingWorkspaces, data: workspaces } =
    useWorkspacesForUserQuery();
  const user = useUserStore(userStoreSelector.user);
  const currentWorkspaceId = useActiveWorkspaceId();

  const currentAndOtherWorkspaces = useMemo<
    [MinimumWorkspace | undefined, MinimumWorkspace[]]
  >(() => {
    if (!workspaces) {
      return [undefined, []];
    }
    return workspaces.reduce(
      (prev, curr) => {
        if (curr.id === currentWorkspaceId) {
          return [curr, prev[1]];
        }
        return [prev[0], [...prev[1], curr]];
      },
      [undefined, []] as [MinimumWorkspace | undefined, MinimumWorkspace[]]
    );
  }, [currentWorkspaceId, workspaces]);

  if (
    !isEnabled ||
    !user ||
    isLoadingWorkspaces ||
    !currentAndOtherWorkspaces[0]
  ) {
    return null;
  }
  return currentAndOtherWorkspaces;
};
